import { message } from 'antd';
import { useHrEmployeeStore } from '../stores/hr-employee-store';
import { customInstance } from '@/shared/api/mutator/custom-instance';
import { Employee } from '../types';

export function useEmployees() {
  const store = useHrEmployeeStore();

  const fetchEmployees = async (page = 0, size = 20) => {
    store.setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), size: String(size) });
      if (store.searchTerm) params.append('searchTerm', store.searchTerm);
      if (store.statusFilter) params.append('status', store.statusFilter);
      if (store.departmentFilter) params.append('departmentId', String(store.departmentFilter));

      const endpoint = store.searchTerm
        ? `/api/hr/employees/search?${params}`
        : store.statusFilter || store.departmentFilter
        ? `/api/hr/employees/filter?${params}`
        : `/api/hr/employees?${params}`;

      const data = await customInstance<any>({ url: endpoint, method: 'GET' });
      store.setEmployees(data.content ?? data, data.totalElements);
    } catch {
      message.error('Failed to load employees');
    } finally {
      store.setLoading(false);
    }
  };

  const fetchEmployee = async (id: number) => {
    try {
      const data = await customInstance<Employee>({ url: `/api/hr/employees/${id}`, method: 'GET' });
      store.setActiveEmployee(data);
      return data;
    } catch {
      message.error('Failed to load employee');
    }
  };

  const createEmployee = async (dto: Partial<Employee>) => {
    try {
      const data = await customInstance<Employee>({ url: '/api/hr/employees', method: 'POST', data: dto });
      message.success('Employee created successfully');
      await fetchEmployees();
      return data;
    } catch {
      message.error('Failed to create employee');
    }
  };

  const updateEmployee = async (id: number, dto: Partial<Employee>) => {
    try {
      const data = await customInstance<Employee>({ url: `/api/hr/employees/${id}`, method: 'PUT', data: dto });
      message.success('Employee updated successfully');
      await fetchEmployees();
      return data;
    } catch {
      message.error('Failed to update employee');
    }
  };

  const deleteEmployee = async (id: number) => {
    try {
      await customInstance({ url: `/api/hr/employees/${id}`, method: 'DELETE' });
      message.success('Employee deleted');
      await fetchEmployees();
    } catch {
      message.error('Failed to delete employee');
    }
  };

  const generateEmployeeNumber = async (): Promise<string | undefined> => {
    try {
      return await customInstance<string>({ url: '/api/hr/employees/generate-employee-number', method: 'GET' });
    } catch {
      return undefined;
    }
  };

  return {
    ...store,
    fetchEmployees,
    fetchEmployee,
    createEmployee,
    updateEmployee,
    deleteEmployee,
    generateEmployeeNumber,
  };
}
